var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Globe as ThreeGlobe } from "../private/objects/globe";
import { LayeredImageryProvider } from "../private/data/imagery";
import { assetFromEndpoint, CesiumIonImageryAsset, CesiumIonTerrainAsset } from "../private/data/cesium/assets";
import { BingMapsImagerySet } from "../private/data/bing";
import { OpenStreetMapImageryProvider } from "../private/data/imagery/OpenStreetMapImageryProvider";
import { TMSImageryProvider } from "../private/data/imagery/TMSImageryProvider";
import { TessellatorTerrainProvider } from "../private/data/terrain";
import { WebMercatorTilingScheme } from "../private/math/tiling";
import { LayeredTerrainProvider } from "../private/data/terrain/LayeredTerrainProvider";
import { Cartographic } from "../private/math";
/**
 * Implementation of IGlobe
 */
export class Globe {
    constructor(renderer) {
        this.visible = true;
        this.newLayers = null;
        this.imageryBounds = [];
        this.terrainBounds = [];
        this._imageryProviders = [];
        this._terrainProviders = [];
        this._tileBudget = 1024;
        this.renderer = renderer;
    }
    getImageryBounds() {
        return this.imageryBounds;
    }
    getTerrainBounds() {
        return this.terrainBounds;
    }
    getVisible() {
        return this.visible;
    }
    setVisible(visible) {
        this.visible = visible;
    }
    get attributions() {
        var _a, _b;
        return (_b = (_a = this.inner) === null || _a === void 0 ? void 0 : _a.attributions) !== null && _b !== void 0 ? _b : [];
    }
    get tileBudget() {
        return this._tileBudget;
    }
    set tileBudget(v) {
        this._tileBudget = v;
        if (this.inner !== undefined) {
            this.inner.tileBudget = v;
        }
    }
    setTileLayers(inImageryLayers, inTerrainLayers = []) {
        const loader = () => __awaiter(this, void 0, void 0, function* () {
            const newTerrainLayers = [];
            for (const layer of inTerrainLayers) {
                switch (layer.type) {
                    case "cesium": {
                        const terrainEndpoint = yield this.cesium.getAssetEndpoint(layer.assetId);
                        if (terrainEndpoint.isExternal)
                            throw new Error();
                        const terrainAsset = yield assetFromEndpoint(terrainEndpoint.endpoint);
                        if (!(terrainAsset instanceof CesiumIonTerrainAsset))
                            throw new Error();
                        newTerrainLayers.push(terrainAsset.createTerrainProvider());
                        break;
                    }
                    // Unrecognized layer type
                    default:
                        throw new Error("Unreachable");
                }
            }
            const newImageryLayers = [];
            for (const layer of inImageryLayers) {
                switch (layer.type) {
                    // OpenStreetMap layer
                    case "osm": {
                        newImageryLayers.push(new OpenStreetMapImageryProvider());
                        break;
                    }
                    // OpenStreetMap layer
                    case "tms": {
                        newImageryLayers.push(yield TMSImageryProvider.create(layer.url));
                        break;
                    }
                    case "cesium": {
                        if (this.cesium !== null) {
                            // Load the asset endpoint and check that it's an internal cesium asset.
                            const endpoint = yield this.cesium.getAssetEndpoint(layer.assetId);
                            if (endpoint.isExternal)
                                throw new Error();
                            // Grab the asset description and assert that it's an imagery asset
                            const asset = yield assetFromEndpoint(endpoint.endpoint);
                            if (!(asset instanceof CesiumIonImageryAsset))
                                throw new Error();
                            // Load the image provider
                            newImageryLayers.push(asset.createImageryProvider());
                        }
                        break;
                    }
                    // Bing maps layer (loaded via cesium)
                    case "bing": {
                        // Parse the imagery type to cesium's well known asset IDs for the bing maps providers
                        const assetId = bingMapsTypeToId(layer.mapType);
                        // Load the asset endpoint and check that it's an external cesium asset (bing maps is external).
                        const endpoint = yield this.cesium.getAssetEndpoint(assetId);
                        if (!endpoint.isExternal)
                            throw new Error();
                        // Grab the asset description and assert that it's an imagery asset
                        const asset = yield BingMapsImagerySet.fromEndpoint(endpoint.endpoint);
                        // Load the image provider
                        newImageryLayers.push(asset.createImageryProvider());
                        break;
                    }
                    // Unrecognized layer type
                    default: {
                        throw new Error("Unreachable");
                    }
                }
            }
            this._imageryProviders = [...inImageryLayers];
            this._terrainProviders = [...inTerrainLayers];
            return [newImageryLayers, newTerrainLayers];
        });
        // Produce a wrapper promise that allows
        return new Promise((resolve, reject) => {
            loader().then(([imageryLayers, terrainLayers]) => {
                this.newLayers = {
                    imageryLayers,
                    terrainLayers,
                    resolve,
                };
            }, reject);
        });
    }
    getTerrainHeight(position) {
        if (this.inner === undefined) {
            return 0;
        }
        const cartographic = new Cartographic(position.longitude, position.latitude, 0);
        return this.inner.getTerrainHeight(cartographic);
    }
    getImageryLayers() {
        return this._imageryProviders;
    }
    getTerrainLayers() {
        return this._terrainProviders;
    }
    /**
     * Function that should be called once per frame prior to rendering to check if a new set of imagery layers has
     * been provided and we should replace the old globe.
     */
    checkLayers() {
        if (this.newLayers !== null) {
            // Replace our cached metadata with metadata from the new layers
            this.imageryBounds.length = 0;
            this.newLayers.imageryLayers.forEach((layer) => {
                this.imageryBounds.push({
                    min: layer.region.southwest,
                    max: layer.region.northeast,
                });
            });
            this.terrainBounds.length = 0;
            this.newLayers.terrainLayers.forEach((layer) => {
                this.terrainBounds.push({
                    min: layer.region.southwest,
                    max: layer.region.northeast,
                });
            });
            // Build our new layered provider and tile cache
            const tileProvider = new LayeredImageryProvider(this.newLayers.imageryLayers);
            let terrainProvider;
            if (this.newLayers.terrainLayers.length === 0) {
                terrainProvider = new TessellatorTerrainProvider(new WebMercatorTilingScheme());
            }
            else {
                terrainProvider = new LayeredTerrainProvider(this.newLayers.terrainLayers);
            }
            // Create a new globe and clear the newLayers ready for the next call to setTileLayers
            this.inner = new ThreeGlobe(terrainProvider, tileProvider, this.renderer.tileCompositorPass, this.renderer.requestManager);
            this.inner.tileBudget = this._tileBudget;
            // Call the callback to resolve the Promise with the associated setTileLayers that requested this set of
            // imagery layers.
            this.newLayers.resolve();
            this.newLayers = null;
        }
    }
}
function bingMapsTypeToId(name) {
    switch (name) {
        case "aerial": {
            return 2;
        }
        case "aerialWithLabels": {
            return 3;
        }
        case "road": {
            return 4;
        }
        default: {
            throw new Error("unreachable");
        }
    }
}
